import { TableRow, TableCell, Button, Chip } from '@heroui/react';
import React from 'react';
import { FiEye, FiShoppingCart } from 'react-icons/fi';

import {
  LoadingTable,
  LoadingTableColumn,
} from '../../../../../components/Table';
import type { OrderWithCustomProducts } from '../../../data/utils/types';
import {
  EMPTY_VALUE,
  formatOrderCreatedAt,
  formatOrderDeliveryDate,
  getOrderStatusColor,
  getOrderStatusLabel,
} from '../../../utils';

interface OrderTableProps {
  orders: OrderWithCustomProducts[];
  isLoading?: boolean;
  onOrderClick?: (orderId: string) => void;
}

interface EntityCellProps {
  name: string;
  /** Short code CS reads out to look the entity up. */
  referenceCode?: string | null;
}

const EntityCell: React.FC<EntityCellProps> = ({ name, referenceCode }) => (
  <div className="flex flex-col">
    <span className="text-sm text-foreground">{name}</span>
    {referenceCode && (
      <span className="text-xs text-default-500">{referenceCode}</span>
    )}
  </div>
);

export const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  isLoading = false,
  onOrderClick,
}) => {
  const handleViewOrder = (orderId: string) => {
    onOrderClick?.(orderId);
  };

  const columns: LoadingTableColumn[] = [
    { label: 'Order Number', className: 'pl-6' },
    { label: 'Buyer', className: 'hidden md:table-cell' },
    { label: 'Distributor', className: 'hidden lg:table-cell' },
    { label: 'Status', className: 'hidden md:table-cell' },
    { label: 'Products', className: 'hidden lg:table-cell' },
    { label: 'Created', className: 'hidden lg:table-cell' },
    { label: 'Delivery', className: 'hidden lg:table-cell' },
    { label: 'Actions', width: 80, className: 'pr-6' },
  ];

  const renderRow = (order: OrderWithCustomProducts) => {
    // Get buyer and supplier names
    const buyerName =
      order.buyer?.displayName || order.buyer?.name || EMPTY_VALUE;
    const supplierName = order.supplier?.name || EMPTY_VALUE;
    const orderNumber = order.referenceId || order.id;

    return (
      <TableRow key={order.id}>
        <TableCell className="pl-6">
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{orderNumber}</span>
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <EntityCell
            name={buyerName}
            referenceCode={order.buyer?.referenceId}
          />
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <EntityCell name={supplierName} referenceCode={order.supplierRefId} />
        </TableCell>
        <TableCell className="hidden md:table-cell">
          {order.status ? (
            <Chip
              size="sm"
              variant="flat"
              color={getOrderStatusColor(order.status)}
            >
              {getOrderStatusLabel(order.status)}
            </Chip>
          ) : (
            <span className="text-sm text-default-500">{EMPTY_VALUE}</span>
          )}
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="font-medium text-foreground">
            {order.products.length}
          </span>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="text-sm text-foreground whitespace-nowrap">
            {formatOrderCreatedAt(order.createdAt)}
          </span>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="text-sm text-foreground whitespace-nowrap">
            {formatOrderDeliveryDate(order.deliveryDate)}
          </span>
        </TableCell>
        <TableCell className="pr-6">
          <div className="flex gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              color="primary"
              onPress={() => handleViewOrder(order.id)}
              aria-label="View order"
            >
              <FiEye className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <LoadingTable
      columns={columns}
      data={orders}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={orders.length === 0}
      rowCount={5}
      ariaLabel="Orders table"
      emptyState={{
        title: 'No orders found',
        description: 'There are no orders available.',
        icon: FiShoppingCart,
        iconBgClass: 'bg-primary-50',
        iconColorClass: 'text-primary-500',
      }}
    />
  );
};
